import { FC, useEffect, useState } from "react";
import { useTrans } from "../../../hooks";
import {
    Button,
    Input,
    InputGroup,
    InputLeftElement,
    VStack,
} from "@chakra-ui/react";
import { School, SchoolApi } from "../../../apis";
import { Controller, useForm } from "react-hook-form";
import { useRecoilValue } from "recoil";
import { ActiveSchoolOrCompanyAtom } from "../../../atoms";
import { AppFilterContainer } from "../../../containers";
import {
    AppFormControl,
    AppIcon,
    AppMultiSelector,
    AppMultiSelectorOption,
    AppMultiSelectorOptionType,
} from "../../../components";
import { APPC } from "../../../config";

export type InternshipScheduleStudentFilterDataType = {
    user_search: string;
};

const defaultValues: InternshipScheduleStudentFilterDataType = {
    user_search: "",
};

export const InternshipScheduleStudentFilter: FC<{
    filterData: any;
    setFilterData: (data: any) => void;
    children: JSX.Element;
}> = ({ filterData, setFilterData, children }) => {
    // hook
    const { t } = useTrans();
    const activeSchoolOrCompany = useRecoilValue(ActiveSchoolOrCompanyAtom);

    // state & const
    const [isCompany, setIsCompany] = useState(false);
    const [selectedSchools, setSelectedSchools] = useState<
        AppMultiSelectorOption[]
    >([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const PARTNERSHIPSTATUS = APPC.BE.Partnership.STATUS;

    const { handleSubmit, reset, control } =
        useForm<InternshipScheduleStudentFilterDataType>({
            defaultValues: filterData,
            mode: "onSubmit",
        });

    // handler
    const onSubmitHandler = (
        formData: InternshipScheduleStudentFilterDataType
    ) => {
        const filterParams: Record<string, string | string[]> = {};
        Object.keys(formData).forEach((key) => {
            if (formData[key]) {
                filterParams[key] = formData[key];
            }
        });
        if (selectedSchools.length > 0) {
            filterParams["schools.id"] = selectedSchools?.map(
                (item) => item.value
            );
        }
        setFilterData(filterParams);
    };

    const loadSchoolOptions = async (
        page: number,
        search: string,
        pageSize: number
    ): Promise<AppMultiSelectorOptionType> => {
        const params = {
            itemsPerPage: pageSize,
            [`order[name]`]: "asc",
            ["partnerships.company.id"]: activeSchoolOrCompany?.["@id"] ?? "",
            ["partnerships.status"]: PARTNERSHIPSTATUS.STATUS_APPROVED,
            pagination: true,
        };

        if (search) {
            params["name"] = search;
        }

        const { response } = await SchoolApi.getCollectionMyPartnership<School>(
            page,
            params
        );

        if (response !== null) {
            const newOptions =
                response.items?.map((e) => ({
                    value: SchoolApi.toResourceIRI(e.id),
                    label: `${e.id} ${e.name}`,
                })) || [];
            return {
                data: newOptions,
                totalItems: response.totalItems,
            };
        }

        return { data: [], totalItems: 0 };
    };

    const resetForm = () => {
        setFilterData({});
        setSelectedSchools([]);
        reset(defaultValues);
    };

    useEffect(() => {
        setIsCompany(`${activeSchoolOrCompany?.["@id"]}`.includes("companies"));
    }, []);

    return (
        <AppFilterContainer
            onReset={resetForm}
            triggerElement={children}
            isModalOpen={isModalOpen}
            zIndex={"1400"}
        >
            <form
                id="app-filter-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                <VStack gap={4} alignItems={"stretch"} justify={"start"}>
                    <AppFormControl label={t("cmn:label.search")}>
                        <Controller
                            control={control}
                            name={"user_search"}
                            defaultValue={filterData?.user_search || ""}
                            render={({ field }) => (
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <AppIcon name="ics-search" w="1rem" />
                                    </InputLeftElement>
                                    <Input {...field} />
                                </InputGroup>
                            )}
                        />
                    </AppFormControl>
                    {isCompany && (
                        <AppFormControl label={t("ent.User:school.label")}>
                            <AppMultiSelector
                                onSelect={(data) => {
                                    setSelectedSchools(data);
                                    setIsModalOpen(false);
                                }}
                                loadOptions={loadSchoolOptions}
                                selectedOpt={selectedSchools}
                                subject={t("ent.User:school.label")}
                                triggerElement={
                                    <Button
                                        size={"sm"}
                                        variant={"secondary"}
                                        onClick={() => setIsModalOpen(true)}
                                    >
                                        {t("padm.UserForm:button.selectSchool")}
                                    </Button>
                                }
                                isModal={true}
                                isSearch={true}
                                selectorRows={8}
                                maxSelectOptions={undefined}
                                setIsModalOpen={setIsModalOpen}
                            />
                        </AppFormControl>
                    )}
                </VStack>
            </form>
        </AppFilterContainer>
    );
};
